import React, { useEffect, useState } from 'react'
import { FlatList, View } from 'react-native'
import { ATLSpacing, ATLTextBody, SpacingSizesEnum } from '@appsimples/atlantica-react-native'
import { NotificationListItem } from './NotificationListItem'
import { atlTheme } from '../../atlTheme'
import { getModuleIconForNotification, mountNotificationBody } from '../../constants/AppModules'
import { formatMessage } from '../../constants/strings/intl/intlHooks'
import { messages } from '../../constants/strings/messages'
import { showMessageSnackbarWithAction } from '../../components/snackbar/Snackbar'
import { shortMonth } from '../../constants/shortMonth'
import { GetNotifications_listUserNotifications } from '../../services/types/getNotifications'
import styled from 'styled-components/native'
import { deleteOwnNotification } from '../../services/main/deleteOwnNotification'
import { FeatureManager } from '../../FeatureManager'
import { changeShouldRefreshNotifications } from '../../services/main/changeShouldRefreshNotifications'

const NotificationListContainer = styled.View`
  margin-top: ${atlTheme.SpacingAtomic}px;
`

interface SwipeableListProps {
  data: GetNotifications_listUserNotifications[]
}

export const NotificationSwipeableList: React.FunctionComponent<SwipeableListProps> = props => {
  const [isScrollEnabled, setScrollEnabled] = useState(true)
  const [data, setData] = useState(props.data)
  const [lastRemovedItem, setLastRemovedItem] = useState({ item: props.data[0], index: 0 })

  useEffect(() => {
    changeShouldRefreshNotifications(true)
  }, [])

  const undoNotificationRemoval = () => {
    const { index, item } = lastRemovedItem
    const restoredNotifications = data
      .slice(0, index)
      .concat(item)
      .concat(data.slice(index + 1, data.length))

    setData(restoredNotifications)
  }

  const updateDataOnDismiss = (id: string) => {
    const removedItem = data.find(item => item.id === id)
    const snackbarTime = 3000

    setLastRemovedItem({ item: removedItem, index: data.indexOf(removedItem) })
    setData(data.filter(item => item.id !== id))

    FeatureManager.useSnackBarUndo &&
      showMessageSnackbarWithAction(
        formatMessage(messages.dashboard_snackbarMessage),
        snackbarTime,
        formatMessage(messages.dashboard_undoLabel),
        undoNotificationRemoval,
        atlTheme.ColorComplementary2,
        atlTheme.ColorBrand2Low,
        atlTheme.FontFamilyBrand
      )
    !FeatureManager.useNotificationMock && deleteOwnNotification(removedItem.id)
  }

  const renderItem = (item, index) => {
    const date = new Date(Number(item.createdAt))
    const month = shortMonth[date.getMonth()]
    return (
      <NotificationListItem
        itemId={item?.id}
        title={`${date.getDate()} ${month} ${date.getFullYear()}`}
        description={mountNotificationBody(item?.type)}
        leftIcon={getModuleIconForNotification(item?.group, item?.type)}
        setScrollEnabled={setScrollEnabled}
        updateDataOnDismiss={updateDataOnDismiss}
        index={index}
        group={item?.group}
      />
    )
  }

  const EmptyState = () => {
    return (
      <View style={{ backgroundColor: atlTheme.ColorSurface, borderBottomLeftRadius: 10, borderBottomRightRadius: 10 }}>
        <ATLTextBody
          labelBaseProps={{
            multilineLimit: 1,
            fontSize: atlTheme.FontSizeHeading,
            lineHeight: atlTheme.LineHeightHeading,
            fontWeight: atlTheme.FontWeight500,
            textColor: atlTheme.ColorBrand1Default,
          }}
        >
          {formatMessage(messages.notificationsScreen_emptyCard_title)}
        </ATLTextBody>
        <ATLTextBody labelBaseProps={{ multilineLimit: 2, textColor: atlTheme.ColorMediumContrast }}>
          {formatMessage(messages.notificationsScreen_emptyCard_message)}
        </ATLTextBody>
      </View>
    )
  }

  const Footer = () => {
    return <ATLSpacing type={SpacingSizesEnum.marginal} />
  }

  return (
    <NotificationListContainer>
      <FlatList
        data={data}
        keyExtractor={item => item.id}
        renderItem={({ item, index }) => renderItem(item, index)}
        ListFooterComponent={Footer}
        ListEmptyComponent={EmptyState}
        scrollEnabled={isScrollEnabled}
      />
    </NotificationListContainer>
  )
}
